import type { DeliverySelection } from './types';

/**
 * Config-service keys that name a POST-phase enrichment *step* rather than a
 * content field. Kept in lock-step with `enrichment-run-payload.ts` (creation):
 * these two drive the `searchDescriptors` object, everything else is a plain
 * `contentAttributes` member. Duplicated here rather than imported so the shared
 * panel carries no dependency on a feature module. Exported so callers (e.g. the
 * delivery wizard's preview step) can tell a step key apart from a content field
 * without re-deriving the list.
 */
export const STEP_KEYS = {
  searchDescriptors: 'search_descriptors',
  phraseDescriptions: 'phrase_descriptions',
} as const;

/** Every step-key value, for a plain `.has(key)` membership check. */
export const STEP_KEY_VALUES: ReadonlySet<string> = new Set(Object.values(STEP_KEYS));

/** The attribute slice appended to an approval payload when delivering a subset. */
export interface DeliveryAttributePayload {
  /** Selected content fields, in the config-service's stable key form. */
  contentAttributes: string[];
  /** Present only when the run offered the descriptor steps — see below. */
  searchDescriptors?: { enabled: boolean; phraseDescriptions: boolean };
}

/**
 * Turns a panel selection into the attribute slice the approval endpoints carry.
 * Selected keys split the same two ways as creation: the descriptor steps become
 * the `searchDescriptors` object, everything else `contentAttributes`.
 *
 * The `searchDescriptors` object is asserted only when the run actually offered a
 * descriptor step; otherwise it is omitted so we never override a server default
 * with an explicit `false` for something this run never produced.
 */
export function buildDeliveryAttributePayload(
  selection: DeliverySelection,
): DeliveryAttributePayload {
  const selected = new Set(selection.attributeKeys);
  const available = new Set(selection.availableKeys);

  const contentAttributes = selection.attributeKeys.filter(
    (key) => key !== STEP_KEYS.searchDescriptors && key !== STEP_KEYS.phraseDescriptions,
  );

  const offersDescriptors =
    available.has(STEP_KEYS.searchDescriptors) || available.has(STEP_KEYS.phraseDescriptions);

  return {
    contentAttributes,
    ...(offersDescriptors
      ? {
          searchDescriptors: {
            enabled: selected.has(STEP_KEYS.searchDescriptors),
            phraseDescriptions: selected.has(STEP_KEYS.phraseDescriptions),
          },
        }
      : {}),
  };
}
